import React, { useEffect, useId, useMemo, useRef, useState } from "react";
import type { Tier } from "../types";
import {
  badgeCode,
  badgeInitials,
  coinShineLayer,
  composeBadgeSvg,
  prepareInlineSvg,
  tierStyle,
  useBadgeLib,
  type BadgeArtVariant,
} from "./badgeLib";
import { loadLegacyBadgeImage } from "./legacyBadgeImage";
import "./badgeArt.css";

export type BadgeArtAnimate = "none" | "idle" | "reveal";

/** Named sizes in CSS pixels. `size` also takes any number. */
export const BADGE_ART_SIZES = {
  xs: 20,
  sm: 32,
  md: 64,
  lg: 96,
  xl: 160,
  hero: 280,
} as const;
export type BadgeArtSizeName = keyof typeof BADGE_ART_SIZES;

/** How long the full reveal takes, from mount (or replay) until the counter chip lands. */
export const BADGE_REVEAL_MS = 1700;
/** The reveal under prefers-reduced-motion: a plain fade. */
export const BADGE_REVEAL_MS_REDUCED = 400;

export interface BadgeArtProps {
  category: string;
  tier: Tier | string;
  /** What the art shows; null for badges without a number. */
  number: number | null;
  /** Copies held; above 1 the art gets the "x3" counter chip. */
  count?: number;
  /** CSS pixels (square), or a named size. */
  size: number | BadgeArtSizeName;
  variant: BadgeArtVariant;
  animate?: BadgeArtAnimate;
  /** Not earned yet: greyed out, no idle motion. */
  locked?: boolean;
  className?: string;
  /** Badge code, for the old-image fallback. Defaults to CATEGORY_NUMBER. */
  code?: string;
  /** Badge name, for the accessible label and the initials fallback. */
  name?: string;
  /** Only the base, as a dark silhouette that shakes (the cinematic's sealed state). */
  sealed?: boolean;
  /** Change it to play the reveal again (animate="reveal"). */
  revealKey?: string | number;
  /** Called once the reveal has played (BADGE_REVEAL_MS, or the reduced-motion fade). */
  onRevealEnd?: () => void;
  style?: React.CSSProperties;
}

type Mode = "pending" | "svg" | "gcs" | "initials";

function prefersReducedMotion(): boolean {
  try {
    return typeof window !== "undefined" && !!window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
  } catch {
    return false;
  }
}

// A stable per-instance offset so a shelf of badges does not breathe in lockstep.
function idleDelay(seed: string): string {
  let h = 0;
  for (let i = 0; i < seed.length; i++) h = (h * 31 + seed.charCodeAt(i)) | 0;
  return `${-((Math.abs(h) % 4000) / 1000)}s`;
}

/**
 * A badge drawn from the parts library, inline, with its layers animatable.
 * Falls back to the old GCS image, then to a tier-coloured initials tile.
 */
const BadgeArt: React.FC<BadgeArtProps> = ({
  category,
  tier,
  number,
  count,
  size,
  variant,
  animate = "none",
  locked = false,
  className,
  code: codeProp,
  name,
  sealed = false,
  revealKey,
  onRevealEnd,
  style,
}) => {
  const { lib, error } = useBadgeLib();
  const reactId = useId();
  const prefix = useMemo(() => `bdg${reactId.replace(/[^A-Za-z0-9]/g, "")}`, [reactId]);
  const px = typeof size === "number" ? size : BADGE_ART_SIZES[size] ?? BADGE_ART_SIZES.md;
  const code = badgeCode({ category, number, code: codeProp });
  const motion: BadgeArtAnimate = locked && animate === "idle" ? "none" : animate;
  const tstyle = tierStyle(String(tier));

  const raw = useMemo(
    () => (lib ? composeBadgeSvg(lib, { category, tier, number, count, code, variant }) : null),
    [lib, category, tier, number, count, code, variant]
  );
  const wantShine = variant === "full" && tstyle.kind === "coin" && motion === "idle" && !sealed;
  const html = useMemo(
    () => (raw ? prepareInlineSvg(raw, prefix, { shine: wantShine && lib ? coinShineLayer(lib, String(tier)) : null }) : null),
    [raw, prefix, wantShine, lib, tier]
  );

  // Fallback chain: the library loaded but cannot draw this badge, or the library failed.
  const needFallback = !raw && (!!lib || !!error);
  const [legacy, setLegacy] = useState<{ code: string; url: string | null } | null>(null);
  const [imgFailed, setImgFailed] = useState(false);
  useEffect(() => {
    if (!needFallback) return undefined;
    let live = true;
    setImgFailed(false);
    loadLegacyBadgeImage(code).then((url) => {
      if (live) setLegacy({ code, url });
    });
    return () => {
      live = false;
    };
  }, [needFallback, code]);

  let mode: Mode = "pending";
  if (html) mode = "svg";
  else if (needFallback && legacy?.code === code) mode = legacy.url && !imgFailed ? "gcs" : "initials";

  const ready = mode !== "pending";
  const endRef = useRef(onRevealEnd);
  useEffect(() => {
    endRef.current = onRevealEnd;
  }, [onRevealEnd]);
  useEffect(() => {
    if (motion !== "reveal" || !ready) return undefined;
    const t = window.setTimeout(
      () => endRef.current?.(),
      prefersReducedMotion() ? BADGE_REVEAL_MS_REDUCED : BADGE_REVEAL_MS
    );
    return () => window.clearTimeout(t);
  }, [motion, ready, revealKey]);

  const classes = [
    "bdg-art",
    `bdg-art--${variant}`,
    motion === "none" ? "bdg-art--still" : `bdg-art--${motion}`,
    `bdg-art--${tstyle.kind}`,
    locked ? "bdg-art--locked" : "",
    sealed ? "bdg-art--sealed" : "",
    className || "",
  ]
    .filter(Boolean)
    .join(" ");

  const baseLabel = name || `${category.replace(/_/g, " ").toLowerCase()}${number !== null && number !== undefined ? ` ${number}` : ""}`;
  const label =
    (/\bbadge$/i.test(baseLabel) ? baseLabel : `${baseLabel} badge`) +
    (count && count > 1 ? `, earned ${count} times` : "") +
    (locked ? ", not earned yet" : "");

  const stageKey = `${revealKey ?? ""}`;
  let stage: React.ReactNode;
  if (mode === "svg" && html) {
    stage = <span key={stageKey} className="bdg-art__stage" dangerouslySetInnerHTML={{ __html: html }} />;
  } else if (mode === "gcs" && legacy?.url) {
    stage = (
      <span key={stageKey} className="bdg-art__stage">
        <img className="bdg-art__img" src={legacy.url} alt="" draggable={false} onError={() => setImgFailed(true)} />
      </span>
    );
  } else if (mode === "initials") {
    stage = (
      <span key={stageKey} className="bdg-art__stage">
        <span
          className="bdg-art__initials"
          style={{
            background: tstyle.fill,
            color: tstyle.ink,
            fontSize: Math.max(8, Math.round(px * 0.34)),
            borderWidth: Math.max(1.5, Math.round(px * 0.03 * 10) / 10),
          }}
        >
          {badgeInitials(name, category)}
        </span>
      </span>
    );
  } else {
    stage = <span className="bdg-art__stage" />;
  }

  return (
    <span
      className={classes}
      role="img"
      aria-label={label}
      data-mode={mode}
      data-tier={tier}
      data-category={category}
      style={{ width: px, height: px, "--bdg-idle-delay": idleDelay(prefix), ...style } as React.CSSProperties}
    >
      {stage}
    </span>
  );
};

export default BadgeArt;
